import { Keyboard } from 'react-native';
import { XStack, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSInput, { INPUT_HEIGHT } from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';

export default function PSCouponCodeInput({
  code,
  appliedCode,
  redeeming = false,
  error,
  onRedeem,
  onRemove,
  onChangeCode,
}: {
  // Controlled so the parent can redeem a typed-but-unredeemed code on pay.
  code: string;
  // Set = applied. Shown in place of the draft so a resumed booking's coupon
  // renders without the user ever having typed it here.
  appliedCode?: string;
  redeeming?: boolean;
  error?: string;
  onRedeem: (code: string) => void;
  // Omitted where a redeem leaves the screen, so there's no applied state.
  onRemove?: () => void;
  onChangeCode: (code: string) => void;
}) {
  const trimmed = code.trim();
  const canRedeem = !redeeming && trimmed.length > 0;

  const redeem = () => {
    if (!canRedeem) return;
    Keyboard.dismiss();
    onRedeem(trimmed);
  };

  if (appliedCode) {
    return (
      <XStack items="center" gap={'$space.md'} alignSelf="stretch">
        {/* PSInput's box, read-only, so the field doesn't jump on apply. */}
        <XStack
          flex={1}
          height={INPUT_HEIGHT}
          px="$md"
          gap="$sm"
          items="center"
          justify="space-between"
          bg="$background"
          rounded="$base"
          borderWidth="$borderWidth.hairline"
          borderColor="$borderColor"
        >
          <PSText.Body2 color="$textDarkMuted" numberOfLines={1} flexShrink={1}>
            {appliedCode}
          </PSText.Body2>
          <XStack gap="$xs" items="center">
            <PSText.Body2 color="$success">Applied</PSText.Body2>
            <PSIcon name="checkmark" color="$success" size={18} />
          </XStack>
        </XStack>
        {/* Locked during a re-price: its success would re-apply the code
            right after the remove. */}
        {onRemove && (
          <PSCircleButton
            variant="bare"
            disabled={redeeming}
            onPress={onRemove}
          >
            <PSIcon name="trash-bin-outline" color="$error" size={28} />
          </PSCircleButton>
        )}
      </XStack>
    );
  }

  return (
    <XStack gap="$sm" items="flex-start" alignSelf="stretch">
      <YStack flex={1}>
        <PSInput
          type="code"
          value={code}
          // Coupon codes are uppercase-only.
          onChangeText={(next) => onChangeCode(next.toUpperCase())}
          onSubmitEditing={redeem}
          disabled={redeeming}
          error={error}
        />
      </YStack>
      {/* PSButton's height is auto, so this row stretches it to the field's
          height; the top-aligned parent keeps it there when a line appears
          under the field. */}
      <XStack height={INPUT_HEIGHT}>
        <PSButton
          title="Apply"
          loading={redeeming}
          loadingTitle="Redeeming"
          disabled={trimmed.length === 0}
          onPress={redeem}
        />
      </XStack>
    </XStack>
  );
}
